import React, { useEffect, useState } from 'react';
import SmallButton from '../../../components/SmallButton';
import SubmitButton from '../../../components/SubmitButton';
import { axiosKK } from '../../../services/networkRequest';
import { ServiceAreaDTO } from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import { AdminNotice } from '../../../components/admin/Shared';

interface Props {
  partnerId: number;
}

export default function ServiceAreasTab({ partnerId }: Props) {
  const [areas, setAreas] = useState<ServiceAreaDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [netError, setNetError] = useState<string>();

  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [prefix, setPrefix] = useState('');
  const [priority, setPriority] = useState('0');
  const [submitting, setSubmitting] = useState(false);
  const [saveBanner, setSaveBanner] = useState<string>();

  const refresh = React.useCallback(() => {
    setLoading(true);
    axiosKK
      .get<{ serviceAreas: ServiceAreaDTO[] }>(`/admin/partners/${partnerId}/service-areas`)
      .then(({ data }) => setAreas(data.serviceAreas || []))
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setLoading(false));
  }, [partnerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setEditingId('new');
    setPrefix('');
    setPriority('0');
  };

  const openEdit = (a: ServiceAreaDTO) => {
    setEditingId(a.id);
    setPrefix(a.postcodePrefix);
    setPriority(String(a.priority));
  };

  const cancel = () => setEditingId(null);

  const submit = () => {
    setSubmitting(true);
    setNetError(undefined);
    setSaveBanner(undefined);
    const payload = {
      postcodePrefix: prefix.trim().toUpperCase(),
      priority: Number(priority) || 0,
    };
    const wasNew = editingId === 'new';
    const request = wasNew
      ? axiosKK.post(`/admin/partners/${partnerId}/service-areas`, payload)
      : axiosKK.put(`/admin/partners/${partnerId}/service-areas/${editingId}`, payload);

    request
      .then(() => {
        setEditingId(null);
        setSaveBanner(wasNew ? 'Service area added.' : 'Service area updated.');
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  const remove = (id: number) => {
    setNetError(undefined);
    setSaveBanner(undefined);
    axiosKK
      .delete(`/admin/partners/${partnerId}/service-areas/${id}`)
      .then(() => {
        setSaveBanner('Service area removed.');
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err));
  };

  return (
    <section className="admin-partner-service-areas">
      <AdminNotice intent="error">{netError}</AdminNotice>
      <AdminNotice intent="success">{saveBanner}</AdminNotice>

      {loading ? (
        <p>Loading service areas…</p>
      ) : (
        <>
          <div className="pricing-save">
            <SubmitButton onClick={openCreate} disabled={editingId !== null}>
              + Add Service Area
            </SubmitButton>
          </div>

          {editingId !== null && (
            <div className="inline-editor">
              <h4>{editingId === 'new' ? 'New service area' : `Edit area #${editingId}`}</h4>
              <div className="inline-editor__row">
                <label>Postcode Prefix</label>
                <input
                  type="text"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                  placeholder="e.g. SW1"
                />
              </div>
              <div className="inline-editor__row">
                <label>Priority</label>
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                />
              </div>
              <div className="inline-editor__actions">
                <SubmitButton
                  onClick={submit}
                  isSubmitting={submitting}
                  disabled={submitting || !prefix.trim()}
                >
                  Save
                </SubmitButton>
                <SmallButton onClick={cancel}>Cancel</SmallButton>
              </div>
            </div>
          )}

          <table className="pricing-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Postcode Prefix</th>
                <th>Priority</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {areas.length === 0 ? (
                <tr>
                  <td colSpan={4}>No service areas configured.</td>
                </tr>
              ) : (
                areas.map((a) => (
                  <tr key={a.id}>
                    <td>{a.id}</td>
                    <td>{a.postcodePrefix}</td>
                    <td>{a.priority}</td>
                    <td>
                      <button
                        type="button"
                        className="pricing-table__remove"
                        onClick={() => openEdit(a)}
                        disabled={editingId !== null}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="pricing-table__remove"
                        onClick={() => remove(a.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
